/**
 * 登録済みの Q&A・公式資料の一覧と、公式資料の登録（issue #109・docs/01 11-3・11-7）。
 *
 * ここでできること:
 *  - いま使われている答え・取り下げた答えを、状態で切り替えて見る
 *  - 1件開いて**訂正・取り下げ**を行う（開いた先の画面）
 *  - **公式資料を登録する**（登録しただけでは使われません。承認して初めて使われます）
 *
 * 一覧は、状態を選んで「この状態で見る」を押したときだけ読み直します（docs/06 原則1）。
 * 1回に50件ずつ読み、続きがあるときは「さらに表示する」を出します（黙って切らない・決定22）。
 * 並びは登録した順の新しいほうからで、読んでいる途中に答えを直しても並びは動きません。
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, messageForStatus } from '../api/client.js';
import { useApi } from '../api/context.js';
import { usePagedList } from '../api/paged-list.js';
import type { AiKnowledgeItem, AiKnowledgePage as AiKnowledgePageData } from '../api/types.js';
import {
  ERASED_TEXT_LABEL,
  knowledgeKindLabel,
  knowledgeStatusLabel,
  summarize,
} from '../ai/labels.js';
import { LoadMoreFooter } from '../components/LoadMoreFooter.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { DataTable } from '../components/ui/DataTable.js';
import { FormCard } from '../components/ui/FormCard.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { withReason } from '../format/sentence.js';
import { formatTableDateTime } from '../format/datetime.js';

const PAGE_SIZE = 50;

interface Banner {
  tone: 'success' | 'error' | 'info';
  message: string;
  nextStep?: ReactNode;
}

export function AiKnowledgePage(): ReactNode {
  const api = useApi();
  // 入力中の状態と、いま一覧に効いている状態を分ける（押すまで一覧は変わらない）
  const [draftStatus, setDraftStatus] = useState('active');
  const [status, setStatus] = useState('active');
  const [reloadKey, setReloadKey] = useState(0);

  // 公式資料の登録
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sourceRef, setSourceRef] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<Banner | null>(null);

  // 状態を変えたら最初から読み直す（前の状態の続きは足さない）
  const list = usePagedList<AiKnowledgeItem>(
    `${status}:${reloadKey}`,
    async (cursor) => {
      const loaded = await api.get<AiKnowledgePageData>('/admin/ai/knowledge', {
        query: { status, limit: PAGE_SIZE, ...(cursor !== null ? { before: cursor } : {}) },
      });
      return { items: loaded.items, next: loaded.nextCursor };
    },
    (item) => item.id,
  );
  const items = list.items;

  async function registerDocument(): Promise<void> {
    // 欄の名前（本文）とお知らせの言葉をそろえる。サーバの言い方（「答え」）のままだと、
    // どの欄のことか分からないため（総点検 J-80）
    if (body.trim() === '') {
      setSaveResult({
        tone: 'error',
        message: '登録できませんでした。本文を入力してください。',
        nextStep: '本文の欄に、資料の中身を貼り付けてください。',
      });
      return;
    }
    setSaving(true);
    setSaveResult(null);
    try {
      const created = await api.post<AiKnowledgeItem>('/admin/ai/knowledge', {
        body: {
          kind: 'doc',
          question: title === '' ? null : title,
          answer: body,
          source_ref: sourceRef,
        },
      });
      setTitle('');
      setBody('');
      setSourceRef('');
      setSaveResult({
        tone: 'success',
        message: '登録しました。承認待ちに入っています（承認するまで使われません）。',
        nextStep: <Link to={`/ai/knowledge/${created.id}`}>いま登録した内容を開く</Link>,
      });
      // 「承認待ち」を見ているときは一覧に足す。読み足した分と表示の位置は残す（issue #424）
      void list.refresh();
    } catch (caught) {
      const reason = caught instanceof ApiError ? caught.message : messageForStatus(0);
      setSaveResult({
        tone: 'error',
        message: withReason('登録できませんでした', reason),
        nextStep: '入力の内容をご確認のうえ、もう一度お試しください。',
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <article>
      <PageHeader
        title="AI先輩の答え"
        description="いま使われている答えと、取り下げた答えの一覧です。1件開くと、直したり取り下げたりできます。"
      />

      <FormCard
        title="この状態で見る"
        submitLabel="この状態で見る"
        submitBusyLabel="読み込んでいます…"
        onSubmit={() => {
          setStatus(draftStatus);
          setReloadKey((n) => n + 1);
        }}
      >
        <label className="ui-field" htmlFor="ai-status">
          <span>状態</span>
          <select
            id="ai-status"
            value={draftStatus}
            onChange={(e) => setDraftStatus(e.target.value)}
          >
            <option value="active">使われています</option>
            <option value="draft">承認待ち</option>
            <option value="disabled">取り下げ済み</option>
          </select>
        </label>
      </FormCard>

      {list.error !== null ? <ErrorBlock failure={list.error} onRetry={list.reload} /> : null}
      {list.error === null && items === null ? (
        <LoadingBlock label="一覧を読み込んでいます…" />
      ) : null}
      {items && items.length === 0 ? <EmptyBlock label="この状態のものはありません。" /> : null}

      {items && items.length > 0 ? (
        <DataTable label="AI先輩の答えの一覧">
          <thead>
            <tr>
              <th scope="col">種類</th>
              <th scope="col">質問・見出し</th>
              <th scope="col">答え</th>
              <th scope="col">出典</th>
              <th scope="col">状態</th>
              <th scope="col">最後に直した日時</th>
              <th scope="col">
                <span className="admin-visually-hidden">開く</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td>{knowledgeKindLabel(item.kind)}</td>
                <td className="ui-cell-wrap">
                  {item.text_erased === true
                    ? ERASED_TEXT_LABEL
                    : summarize(item.question) || '（見出しなし）'}
                </td>
                <td className="ui-cell-wrap">
                  {item.text_erased === true ? '' : summarize(item.answer)}
                </td>
                <td className="ui-cell-soft">{item.source_ref ?? '（出典なし）'}</td>
                <td>{knowledgeStatusLabel(item.status)}</td>
                <td className="ui-cell-date">{formatTableDateTime(item.updated_at)}</td>
                <td>
                  <Link
                    className="ui-button ui-button-secondary ui-button-compact"
                    to={`/ai/knowledge/${item.id}`}
                  >
                    開く
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      ) : null}
      <LoadMoreFooter list={list} />

      <FormCard
        title="公式資料を登録する"
        description="作業手順書・社内規程など、会社が決めた内容を登録します。登録しただけでは使われません（承認してから使われます）。"
        submitLabel="登録する"
        submitBusyLabel="登録しています…"
        busy={saving}
        onSubmit={() => void registerDocument()}
        footer={saveResult ? <ResultBanner {...saveResult} /> : undefined}
      >
        <label className="ui-field" htmlFor="doc-title">
          <span>見出し（省略できます）</span>
          <input
            id="doc-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label className="ui-field" htmlFor="doc-body">
          <span>本文</span>
          <textarea id="doc-body" rows={6} value={body} onChange={(e) => setBody(e.target.value)} />
        </label>
        <label className="ui-field" htmlFor="doc-source-ref">
          <span>出典（例：引越し作業手順書 3-2）</span>
          <input
            id="doc-source-ref"
            type="text"
            value={sourceRef}
            onChange={(e) => setSourceRef(e.target.value)}
          />
        </label>
      </FormCard>
    </article>
  );
}
